import React from 'react';
import Select from 'react-select';
import { withTranslation, WithTranslation } from 'react-i18next';
import { getChildren } from '../../utils/helpers';
import { TargetNode } from '../../models/TargetNode';

interface TargetOption {
  value: string;
  label: string;
}

interface ParentSelectorOwnProps {
  // Plain target node (the `TargetNode` model lands with the container in
  // Phase 7). Only `parent` and the child ids are read — `_id` is required by
  // getChildren's TreeTarget.
  node: TargetNode;
  // Flat target list from the store.
  targets: Array<{ _id: string; name: string }>;
  className?: string;
  onChangeParent: (option: TargetOption | null) => void;
}

const ParentSelector = ({
  node, targets, className, onChangeParent, t,
}: ParentSelectorOwnProps & WithTranslation) => {
  // A target cannot be reparented under its own descendants.
  // getChildren's TreeTarget requires _id; TargetNode marks it optional because
  // it models server data. Only children/_id are read here.
  const childrenIds = getChildren(node as any);
  const parent = node.parent;
  const options: TargetOption[] = targets
    .filter(target => !childrenIds.includes(target._id))
    .map(target => ({ value: target._id, label: target.name }));

  if (!parent) {
    return null;
  }

  options.sort((a, b) => {
    if (a.label > b.label) return 1;
    if (a.label < b.label) return -1;
    return 0;
  });

  // The current parent, as the option object react-select needs. This used to
  // pass the bare id as `value` and lean on `placeholder` to show the name —
  // the same idiom that hid a real bug in QRcodeSelector. Here the name was
  // correct either way, so the only visible change is that the parent now
  // renders as a selected value rather than as placeholder text.
  const selected = options.find(option => option.value === parent) || null;

  return (
    <div className={className}>
      <label>{t('target.editor.label.parent')}</label>
      <Select
        placeholder={selected ? selected.label : ''}
        value={selected}
        options={options}
        onChange={onChangeParent} />
    </div>
  );
};

export default React.memo(withTranslation('common')(ParentSelector));
